import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  AlertCircle,
  ArrowDownLeft,
  ChevronDown,
  DollarSign,
  Eye,
  EyeOff,
  Gamepad2,
  Info,
  Key,
  ListOrdered,
  Mail,
  MoreHorizontal,
  Pencil,
  Percent,
  RefreshCw,
  Scale,
  ShieldCheck,
  Store,
  Users,
  Wallet,
  Network,
  Ban,
  Skull,
  RotateCcw,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { NumberedPagination, SellCreditsCashModal, type SellCreditsCashFormValues, useTimezone, formatInTimezone } from '@kioskgaming/ui';
import {
  useAdminSuperAgentCashFunding,
  useAdminSuperAgentCashWithdrawal,
  useGameProviders,
  usePatchSuperAgent,
  usePatchSuperAgentName,
  usePatchSuperAgentPassword,
  usePatchSuperAgentRates,
  useSuspendAdminSuperAgent,
  useUnsuspendAdminSuperAgent,
  useTerminateAdminSuperAgent,
  usePutSuperAgentGameMappings,
  useSuperAgentDetail,
  useSuperAgentGameMappings,
  useSuperAgentWalletTransactions,
  useUsdWalletSummary,
  QUERY_KEYS,
  usePatchSuperAgentPlayerWithdrawalAutoApprove,
} from '../../../hooks/useApi';
import type { SuperAgentWalletTransactionItem } from '../../../types';
import { usePermission, useRole } from '../../../hooks/useAuth';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbLabelContext';
import { toast } from 'react-hot-toast';
import { formatCurrency, truncateText } from '../../../utils';
import {
  DISPLAY_NAME_MAX_LENGTH,
  getDisplayNameValidationError,
  validateDisplayName,
} from '../../../utils/displayName';
import FinanceCorrectionDebitModal from '../../finance/FinanceCorrectionDebitModal';
import AdminUpdateContactModal from '../../shared/AdminUpdateContactModal';
import AdminUpdateUsernameModal from '../../shared/AdminUpdateUsernameModal';
import SuperAgentPortalWithdrawalLimitsCard from './SuperAgentPortalWithdrawalLimitsCard';
import AdminPlayerWithdrawalAutoApproveCard from '../../shared/AdminPlayerWithdrawalAutoApproveCard';
import SuperAgentHierarchyChart from './SuperAgentHierarchyChart';
import SuperAgentWalletPnlCards from './SuperAgentWalletPnlCards';
import {
  Button as ShadButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn';

const rateToPercent = (value: unknown): string => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0.00%';
  return `${(n * 100).toFixed(2)}%`;
};

const statusClassName = (status?: string): string => {
  if (status === 'active') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'suspended') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const PASSWORD_MIN = 8;
const DETAIL_NAME_DISPLAY_MAX = 40;
const SA_SUSPEND_CONFIRM_WORD = 'suspended';
const SA_TERMINATE_CONFIRM_WORD = 'terminate';
type SuperAgentDetailTab = 'info' | 'hierarchy' | 'agents' | 'shops' | 'transactions' | 'games';
type SaHeaderActionKey =
  | 'change_password'
  | 'change_username'
  | 'update_contact'
  | 'edit_display_name'
  | 'edit_cost_rate'
  | 'credit_wallet'
  | 'withdraw_wallet'
  | 'debit_correction';

function saFormatSignedAmount(n: number): string {
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `-${formatted}`;
  return formatted;
}

function wtFormatDate(iso?: string, timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? formatInTimezone(d, timezone) : '—';
}

function wtSignedDelta(wt: SuperAgentWalletTransactionItem): number {
  const a = Math.abs(Number(wt.amount ?? 0));
  if (wt.direction === 'out') return -a;
  if (wt.direction === 'in') return a;
  return 0;
}

const SuperAgentDetailPage: React.FC = () => {
  const { timezone } = useTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission('agentManagement');
  // KIOSK-720: Adjust Wallet is a write action — operation_admin/platform_admin may only VIEW wallets.
  const canAdjustWalletRole = useRole(['system_admin', 'super_admin', 'finance_admin']);
  const canUpdateContact = useRole(['system_admin', 'super_admin']);
  const canManageCredentials = canUpdateContact;
  const canFinanceCorrect = usePermission('walletManagement') && canAdjustWalletRole;
  const { data, isLoading, error } = useSuperAgentDetail(id, canManage);
  const {
    data: usdWalletSummary,
    isLoading: usdWalletLoading,
    error: usdWalletError,
  } = useUsdWalletSummary(
    { ownerType: 'super_agent', ownerId: id ?? '', currency: 'USD' },
    canManage && !!id,
  );
  const { data: gameMappingsRes, isLoading: gamesLoading } = useSuperAgentGameMappings(id, canManage);
  const { data: gameProvidersRes } = useGameProviders();
  const putSuperAgentGames = usePutSuperAgentGameMappings();
  const patchSuperAgentRates = usePatchSuperAgentRates();
  const patchSuperAgentName = usePatchSuperAgentName();
  const patchSuperAgentDelegate = usePatchSuperAgent();
  const patchSuperAgentPassword = usePatchSuperAgentPassword();
  const suspendSuperAgent = useSuspendAdminSuperAgent();
  const unsuspendSuperAgent = useUnsuspendAdminSuperAgent();
  const terminateSuperAgent = useTerminateAdminSuperAgent();
  const patchPlayerAutoApprove = usePatchSuperAgentPlayerWithdrawalAutoApprove();
  const cashFunding = useAdminSuperAgentCashFunding();
  const cashWithdrawal = useAdminSuperAgentCashWithdrawal();
  const [costRateModalOpen, setCostRateModalOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [costRatePercentInput, setCostRatePercentInput] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<SuperAgentDetailTab>('info');
  const [wtPage, setWtPage] = useState(1);
  const wtLimit = 25;
  const {
    data: wtListRes,
    isLoading: wtLoading,
    error: wtError,
    refetch: refetchWalletTransactions,
    isFetching: wtFetching,
  } = useSuperAgentWalletTransactions(id, { page: wtPage, limit: wtLimit }, activeTab === 'transactions');
  const [gameMode, setGameMode] = useState<'all' | 'selected'>('all');
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  const [gamesUiMode, setGamesUiMode] = useState<'view' | 'edit'>('view');
  const [saSuspendModalOpen, setSaSuspendModalOpen] = useState(false);
  const [saSuspendConfirmText, setSaSuspendConfirmText] = useState('');
  const [saTerminateModalOpen, setSaTerminateModalOpen] = useState(false);
  const [saTerminateConfirmText, setSaTerminateConfirmText] = useState('');

  const applyGameMappingsFromResponse = useCallback(() => {
    const ids = gameMappingsRes?.data?.game_ids ?? [];
    if (ids.length === 0) {
      setGameMode('all');
      setSelectedGameIds(new Set());
    } else {
      setGameMode('selected');
      setSelectedGameIds(new Set(ids));
    }
  }, [gameMappingsRes]);

  useEffect(() => {
    if (gamesUiMode === 'edit') return;
    applyGameMappingsFromResponse();
  }, [gamesUiMode, applyGameMappingsFromResponse]);

  useEffect(() => {
    if (activeTab !== 'games') {
      setGamesUiMode('view');
      applyGameMappingsFromResponse();
    }
  }, [activeTab, applyGameMappingsFromResponse]);

  useEffect(() => {
    setWtPage(1);
  }, [id]);

  const gameProviderItems = useMemo(
    () => gameProvidersRes?.data?.items?.filter((p) => p.status !== 'maintenance') ?? [],
    [gameProvidersRes],
  );

  const selectedGamesViewRows = useMemo(() => {
    if (gameMode !== 'selected') return [];
    const byId = new Map(gameProviderItems.map((p) => [p.id, p]));
    return Array.from(selectedGameIds)
      .map((id) => {
        const p = byId.get(id);
        return p ? { id, name: p.name, code: p.code } : { id, name: id, code: '—' };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [gameMode, selectedGameIds, gameProviderItems]);

  const toggleGameId = useCallback((gid: string) => {
    setSelectedGameIds((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }, []);

  const handleCancelGamesEdit = () => {
    applyGameMappingsFromResponse();
    setGamesUiMode('view');
  };

  const handleSaveGames = async () => {
    if (!id || !canManage || putSuperAgentGames.isLoading) return;
    const payload = gameMode === 'all' ? [] : Array.from(selectedGameIds);
    try {
      await putSuperAgentGames.mutateAsync({ superAgentId: id, game_ids: payload });
      setGamesUiMode('view');
    } catch {
      // toast from hook
    }
  };
  const [cashFundingError, setCashFundingError] = useState<string | null>(null);
  const [withdrawFromWallet, setWithdrawFromWallet] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [cashWithdrawalError, setCashWithdrawalError] = useState<string | null>(null);
  const superAgent = data?.data?.superAgent;
  useBreadcrumbLabel(
    superAgent?.name ? truncateText(superAgent.name, DETAIL_NAME_DISPLAY_MAX) : undefined,
  );
  const [delegatePopups, setDelegatePopups] = useState(false);
  const agents = useMemo(() => data?.data?.agents ?? [], [data?.data?.agents]);
  const shops = data?.data?.shops ?? [];
  const superAgentWallet = data?.data?.super_agent_wallet;
  const agentNameById = useMemo(
    () =>
      agents.reduce<Record<string, string>>((acc, agent) => {
        acc[agent.id] = agent.name;
        return acc;
      }, {}),
    [agents],
  );

  const superAgentCostRate = useMemo(() => {
    const value = Number(superAgent?.costRate ?? 0);
    if (!Number.isFinite(value)) return null;
    return Math.max(0, Math.min(1, value));
  }, [superAgent?.costRate]);

  const withdrawFromWalletNum = parseFloat(withdrawFromWallet);

  useEffect(() => {
    if (superAgentCostRate == null) {
      setCostRatePercentInput('');
      return;
    }
    setCostRatePercentInput((superAgentCostRate * 100).toFixed(2).replace(/\.00$/, ''));
  }, [superAgentCostRate]);

  useEffect(() => {
    setDelegatePopups(!!superAgent?.canDelegatePopups);
  }, [superAgent?.id, superAgent?.canDelegatePopups]);

  if (!id) {
    return <div className="p-6 text-muted-foreground">Invalid super agent.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !superAgent) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Failed to load super agent detail.</p>
      </div>
    );
  }

  const closePasswordModal = () => {
    if (patchSuperAgentPassword.isLoading) return;
    setPasswordModalOpen(false);
    setPassword('');
    setPasswordConfirm('');
    setShowPassword(false);
  };

  const closeNameModal = () => {
    if (patchSuperAgentName.isLoading) return;
    setNameModalOpen(false);
    setNameDraft('');
  };

  const openNameModal = () => {
    setNameDraft(superAgent?.name ?? '');
    setNameModalOpen(true);
  };

  const handleCostRateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !canManage || patchSuperAgentRates.isLoading) return;
    const value = Number.parseFloat(costRatePercentInput);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      toast.error('Cost rate must be between 0 and 100');
      return;
    }
    try {
      await patchSuperAgentRates.mutateAsync({
        superAgentId: id,
        cost_rate: value / 100,
      });
      setCostRateModalOpen(false);
    } catch {
      // Toast from usePatchSuperAgentRates onError
    }
  };

  const closeCreditModal = () => {
    if (cashFunding.isLoading) return;
    setCreditModalOpen(false);
    setCashFundingError(null);
  };

  const closeWithdrawModal = () => {
    if (cashWithdrawal.isLoading) return;
    setWithdrawModalOpen(false);
    setCashWithdrawalError(null);
  };

  const closeSaSuspendModal = () => {
    if (suspendSuperAgent.isLoading) return;
    setSaSuspendModalOpen(false);
    setSaSuspendConfirmText('');
  };

  const closeSaTerminateModal = () => {
    if (terminateSuperAgent.isLoading) return;
    setSaTerminateModalOpen(false);
    setSaTerminateConfirmText('');
  };

  const saSuspendPhraseOk = saSuspendConfirmText.trim().toLowerCase() === SA_SUSPEND_CONFIRM_WORD;
  const saTerminatePhraseOk = saTerminateConfirmText.trim().toLowerCase() === SA_TERMINATE_CONFIRM_WORD;

  const handleSaSuspendConfirmed = async () => {
    if (!id || !saSuspendPhraseOk) return;
    try {
      await suspendSuperAgent.mutateAsync(id);
      closeSaSuspendModal();
    } catch {
      // useSuspendAdminSuperAgent onError
    }
  };

  const handleSaTerminateConfirmed = async () => {
    if (!id || !saTerminatePhraseOk) return;
    try {
      await terminateSuperAgent.mutateAsync(id);
      closeSaTerminateModal();
    } catch {
      // useTerminateAdminSuperAgent onError
    }
  };

  const runSaHeaderAction = (key: SaHeaderActionKey) => {
    switch (key) {
      case 'change_password':
        setPasswordModalOpen(true);
        break;
      case 'change_username':
        setUsernameModalOpen(true);
        break;
      case 'update_contact':
        setContactModalOpen(true);
        break;
      case 'edit_display_name':
        openNameModal();
        break;
      case 'edit_cost_rate':
        setCostRateModalOpen(true);
        break;
      case 'credit_wallet':
        setCashFundingError(null);
        setCreditModalOpen(true);
        break;
      case 'withdraw_wallet':
        setCashWithdrawalError(null);
        setWithdrawModalOpen(true);
        break;
      case 'debit_correction':
        setCorrectionOpen(true);
        break;
      default:
        break;
    }
  };

  const handleSellCreditsSubmit = async (values: SellCreditsCashFormValues) => {
    if (!id || !canManage) return;
    setCashFundingError(null);
    try {
      const res = await cashFunding.mutateAsync({
        superAgentId: id,
        body: {
          credits: values.creditAmount,
          cash_received: values.cashReceived,
          payment_method: values.paymentMethod,
          received_at: values.receivedAt,
          external_reference: values.externalReference,
          note: values.note,
        },
      });
      setCreditModalOpen(false);
      const cashTx = res?.data?.cash_transaction as { id?: string; createdAt?: string } | undefined;
      if (cashTx?.id && cashTx?.createdAt) {
        navigate(
          `/cash-transactions/${cashTx.id}?created_at=${encodeURIComponent(String(cashTx.createdAt))}`,
        );
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Failed to add credits. Please try again.';
      setCashFundingError(message);
    }
  };

  const handleCashWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !canManage) return;
    const amt = parseFloat(withdrawFromWallet);
    if (!Number.isFinite(amt) || amt <= 0) return;
    setCashWithdrawalError(null);
    try {
      await cashWithdrawal.mutateAsync({
        superAgentId: id,
        body: { amount: amt, note: withdrawNote.trim() || undefined },
      });
      setWithdrawFromWallet('');
      setWithdrawNote('');
      setWithdrawModalOpen(false);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Failed to withdraw credits. Please try again.';
      setCashWithdrawalError(message);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !canManage || patchSuperAgentPassword.isLoading) return;
    if (password.length < PASSWORD_MIN) {
      toast.error(`Password must be at least ${PASSWORD_MIN} characters`);
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await patchSuperAgentPassword.mutateAsync({
        superAgentId: id,
        password,
      });
      closePasswordModal();
    } catch {
      // Toast from usePatchSuperAgentPassword onError
    }
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !canManage || patchSuperAgentName.isLoading) return;
    const nameError = getDisplayNameValidationError(nameDraft);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    const trimmed = validateDisplayName(nameDraft);
    if (trimmed === superAgent.name) {
      closeNameModal();
      return;
    }
    try {
      await patchSuperAgentName.mutateAsync({ superAgentId: id, name: trimmed });
      closeNameModal();
    } catch {
      // Toast from hook
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 pb-4">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={activeTab === 'info' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('info')}
              className="gap-2"
            >
              <Info className="h-4 w-4" />
              Information
            </Button>
            <Button
              size="sm"
              variant={activeTab === 'hierarchy' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('hierarchy')}
              className="gap-2"
            >
              <Network className="h-4 w-4" />
              Hierarchy
            </Button>
            <Button
              size="sm"
              variant={activeTab === 'agents' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('agents')}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              Agents
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-background/20 px-1.5 text-[11px]">
                {agents.length}
              </span>
            </Button>
            <Button
              size="sm"
              variant={activeTab === 'shops' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('shops')}
              className="gap-2"
            >
              <Store className="h-4 w-4" />
              Shops
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-background/20 px-1.5 text-[11px]">
                {shops.length}
              </span>
            </Button>
            <Button
              size="sm"
              variant={activeTab === 'transactions' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('transactions')}
              className="gap-2"
            >
              <ListOrdered className="h-4 w-4" />
              Transactions
            </Button>
            {canManage ? (
              <Button
                size="sm"
                variant={activeTab === 'games' ? 'primary' : 'outline'}
                onClick={() => setActiveTab('games')}
                className="gap-2"
              >
                <Gamepad2 className="h-4 w-4" />
                Games
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {activeTab === 'info' ? (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1 min-w-0">
                <h1 className="flex min-w-0 items-center gap-2 text-2xl font-semibold">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
                  <span className="truncate" title={superAgent.name}>
                    {truncateText(superAgent.name, DETAIL_NAME_DISPLAY_MAX)}
                  </span>
                </h1>
                <p className="text-sm text-muted-foreground">{superAgent.email}</p>
                <Badge variant="outline" className={`uppercase text-xs tracking-wide ${statusClassName(superAgent.status)}`}>
                  {superAgent.status}
                </Badge>
              </div>
              {canManage ? (
                <div className="flex shrink-0 flex-col gap-1.5 sm:ml-auto sm:items-end">
                  <span className="text-xs text-muted-foreground">Actions</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <ShadButton
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 w-full min-w-[220px] max-w-[min(100vw-2rem,320px)] justify-between gap-2 px-3 font-normal sm:min-w-[260px]"
                        disabled={
                          patchSuperAgentPassword.isLoading ||
                          patchSuperAgentName.isLoading ||
                          patchSuperAgentRates.isLoading
                        }
                        aria-label="Open super agent actions menu"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <MoreHorizontal className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                          <span className="truncate">Choose an action…</span>
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                      </ShadButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[min(calc(100vw-2rem),18rem)] sm:w-64">
                      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                        Super agent
                      </DropdownMenuLabel>
                      {superAgent.status !== 'terminated' ? (
                        <>
                          <DropdownMenuItem
                            onSelect={() => runSaHeaderAction('change_password')}
                            disabled={patchSuperAgentPassword.isLoading}
                          >
                            <Key className="text-muted-foreground" aria-hidden />
                            Change password
                          </DropdownMenuItem>
                          {canManageCredentials ? (
                            <DropdownMenuItem onSelect={() => runSaHeaderAction('change_username')}>
                              <Pencil className="text-muted-foreground" aria-hidden />
                              Change username
                            </DropdownMenuItem>
                          ) : null}
                          {canUpdateContact ? (
                            <DropdownMenuItem onSelect={() => runSaHeaderAction('update_contact')}>
                              <Mail className="text-muted-foreground" aria-hidden />
                              Update email / phone
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            onSelect={() => runSaHeaderAction('edit_display_name')}
                            disabled={patchSuperAgentName.isLoading}
                          >
                            <Pencil className="text-muted-foreground" aria-hidden />
                            Edit display name
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      <DropdownMenuItem
                        onSelect={() => runSaHeaderAction('edit_cost_rate')}
                        disabled={patchSuperAgentRates.isLoading}
                      >
                        <Percent className="text-muted-foreground" aria-hidden />
                        Edit cost rate
                      </DropdownMenuItem>
                      {superAgent.status === 'active' ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => runSaHeaderAction('credit_wallet')}>
                            <Wallet className="text-muted-foreground" aria-hidden />
                            CDN credit (manual funding)
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => runSaHeaderAction('withdraw_wallet')}>
                            <ArrowDownLeft className="text-muted-foreground" aria-hidden />
                            CDN withdraw (manual)
                          </DropdownMenuItem>
                        </>
                      ) : null}
                      {canFinanceCorrect && superAgent.status !== 'terminated' ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => runSaHeaderAction('debit_correction')}>
                            <Scale className="text-muted-foreground" aria-hidden />
                            Debit correction
                          </DropdownMenuItem>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : null}
            </div>
            <div className="grid gap-3 md:grid-cols-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Phone</p>
                <p className="font-medium">{superAgent.phone || '—'}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Cost rate</p>
                <p className="font-medium">{rateToPercent(superAgent.costRate)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Managed agents</p>
                <p className="font-medium">{agents.length}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-md border p-3 text-sm">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                  <p className="text-muted-foreground">CDN wallet balance</p>
                </div>
                <p className="mt-1 font-semibold tabular-nums text-base">
                  {superAgentWallet != null ? Number(superAgentWallet.balance).toFixed(2) : '0.00'}{' '}
                  <span className="text-sm font-normal text-muted-foreground">credits</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Cash to collect = credits × {superAgentCostRate == null ? '—' : `${(superAgentCostRate * 100).toFixed(0)}%`}
                </p>
              </div>
              <div className="rounded-md border p-3 text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <p className="text-muted-foreground">USD wallet balance</p>
                </div>
                {usdWalletLoading ? (
                  <div className="mt-3 flex justify-center py-1">
                    <LoadingSpinner />
                  </div>
                ) : usdWalletError ? (
                  <p className="mt-1 text-xs text-destructive">Could not load USD balance.</p>
                ) : (
                  <>
                    <p className="mt-1 font-semibold tabular-nums text-base">
                      {formatCurrency(Number(usdWalletSummary?.balanceNet ?? 0))}{' '}
                      <span className="text-sm font-normal text-muted-foreground">net</span>
                    </p>
                    {Number(usdWalletSummary?.balanceGross ?? 0) !==
                    Number(usdWalletSummary?.balanceNet ?? 0) ? (
                      <p className="text-xs text-muted-foreground mt-1 tabular-nums">
                        Gross {formatCurrency(Number(usdWalletSummary?.balanceGross ?? 0))}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            <SuperAgentWalletPnlCards superAgentId={id!} enabled={activeTab === 'info'} />
            {canManage && superAgent.status !== 'terminated' ? (
              <>
              <SuperAgentPortalWithdrawalLimitsCard
                superAgentId={id!}
                limits={superAgent.portalWithdrawalLimits}
                rawMaxPerTransaction={
                  superAgent.maxPortalWithdrawalPerTransaction ??
                  superAgent.max_portal_withdrawal_per_transaction
                }
                rawMaxPerDay={
                  superAgent.maxPortalWithdrawalPerDay ?? superAgent.max_portal_withdrawal_per_day
                }
              />
              <AdminPlayerWithdrawalAutoApproveCard
                level="super_agent"
                snapshot={superAgent.playerWithdrawalAutoApprove}
                saving={patchPlayerAutoApprove.isLoading}
                onSave={async (value) => {
                  await patchPlayerAutoApprove.mutateAsync({
                    superAgentId: id!,
                    withdrawal_auto_approve_threshold: value,
                  });
                }}
              />
              </>
            ) : null}
            {canManage && superAgent.status !== 'terminated' && (
              <div className="rounded-md border p-4 space-y-3 text-sm">
                <p className="font-medium text-foreground">Popup and broadcast delegation</p>
                <p className="text-xs text-muted-foreground">
                  When enabled, this super agent can send in-app popups and bell notifications to managed agents,
                  shops, and players, and can grant popup permissions when creating agents. Disabling cascades and
                  removes delegation flags under this super agent.
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={delegatePopups}
                    onChange={(e) => setDelegatePopups(e.target.checked)}
                  />
                  <span>Allow hierarchy popup and broadcast delegation</span>
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={
                    patchSuperAgentDelegate.isLoading || delegatePopups === !!superAgent.canDelegatePopups
                  }
                  onClick={() => {
                    if (!id) return;
                    void patchSuperAgentDelegate.mutateAsync({
                      superAgentId: id,
                      can_delegate_popups: delegatePopups,
                    });
                  }}
                >
                  {patchSuperAgentDelegate.isLoading ? 'Saving…' : 'Save delegation setting'}
                </Button>
              </div>
            )}
            {canManage && superAgent.status !== 'terminated' && (
              <div className="rounded-md border border-destructive/30 p-4 space-y-3 text-sm">
                <p className="font-medium text-destructive">Governance</p>
                <p className="text-xs text-muted-foreground">
                  Suspend signs the super agent out and blocks portal access. Terminate is permanent: freezes the super
                  agent CDN wallet and suspends every managed agent that is still active.
                </p>
                <div className="flex flex-wrap gap-2">
                  {superAgent.status === 'active' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-amber-600/50 text-amber-900 dark:text-amber-100"
                      onClick={() => {
                        setSaSuspendConfirmText('');
                        setSaSuspendModalOpen(true);
                      }}
                      disabled={
                        suspendSuperAgent.isLoading ||
                        unsuspendSuperAgent.isLoading ||
                        terminateSuperAgent.isLoading
                      }
                    >
                      <Ban className="h-4 w-4 mr-1.5" aria-hidden />
                      Suspend
                    </Button>
                  ) : null}
                  {superAgent.status === 'suspended' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!id || !window.confirm('Reactivate this suspended super agent?')) return;
                        void unsuspendSuperAgent.mutateAsync(id);
                      }}
                      disabled={
                        suspendSuperAgent.isLoading ||
                        unsuspendSuperAgent.isLoading ||
                        terminateSuperAgent.isLoading
                      }
                    >
                      <RotateCcw className="h-4 w-4 mr-1.5" aria-hidden />
                      Unsuspend
                    </Button>
                  ) : null}
                  {superAgent.status === 'active' || superAgent.status === 'suspended' ? (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setSaTerminateConfirmText('');
                        setSaTerminateModalOpen(true);
                      }}
                      disabled={
                        suspendSuperAgent.isLoading ||
                        unsuspendSuperAgent.isLoading ||
                        terminateSuperAgent.isLoading
                      }
                    >
                      <Skull className="h-4 w-4 mr-1.5" aria-hidden />
                      Terminate
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === 'hierarchy' ? (
        <SuperAgentHierarchyChart
          superAgent={{
            id: superAgent.id,
            name: superAgent.name,
            email: superAgent.email,
            status: superAgent.status,
          }}
          agents={agents}
          shops={shops}
        />
      ) : activeTab === 'transactions' ? (
        <Card>
            <CardHeader className="space-y-1">
              <div className="flex flex-row flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-row items-center gap-2">
                    <ListOrdered className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <CardTitle className="text-lg">Wallet transactions</CardTitle>
                  </div>
                  <p className="text-sm text-muted-foreground font-normal">
                    CDN balance movements (<code className="text-xs">wallet_transactions</code>) — purchases, transfers,
                    player top-ups, etc.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={wtFetching}
                  onClick={() => {
                    void refetchWalletTransactions();
                  }}
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${wtFetching ? 'animate-spin' : ''}`} />
                  {wtFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {wtLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : wtError ? (
                <p className="p-6 text-destructive">Could not load wallet activity.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Direction</TableHead>
                          <TableHead>Counterparty</TableHead>
                          <TableHead className="text-right">Change</TableHead>
                          <TableHead className="text-right">Balance before</TableHead>
                          <TableHead className="text-right">Balance after</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(wtListRes?.data?.items ?? []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                              No wallet movements yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          (wtListRes?.data?.items ?? []).map((wt) => {
                            const delta = wtSignedDelta(wt);
                            const cp = wt.counterparty;
                            const counterpartyCell = (() => {
                              if (!cp) return <span className="text-muted-foreground">—</span>;
                              if (cp.ownerType === 'player') {
                                return (
                                  <Link
                                    to={`/users/${cp.ownerId}`}
                                    className="font-medium text-primary hover:underline"
                                  >
                                    Player
                                  </Link>
                                );
                              }
                              if (cp.ownerType === 'shop') {
                                const s = shops.find((x) => x.id === cp.ownerId);
                                return s?.agentId ? (
                                  <Link
                                    to={`/shops/${s.id}`}
                                    className="font-medium text-primary hover:underline"
                                  >
                                    {s.name}
                                  </Link>
                                ) : (
                                  <span className="text-muted-foreground">Shop</span>
                                );
                              }
                              if (cp.ownerType === 'agent') {
                                return (
                                  <Link
                                    to={`/agents/${cp.ownerId}`}
                                    className="font-medium text-primary hover:underline"
                                  >
                                    Agent
                                  </Link>
                                );
                              }
                              return (
                                <span className="text-muted-foreground">
                                  {cp.ownerType} · {cp.ownerId.slice(0, 8)}…
                                </span>
                              );
                            })();
                            return (
                              <TableRow key={wt.id}>
                                <TableCell className="whitespace-nowrap">{wtFormatDate(wt.createdAt, timezone)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="uppercase text-[10px]">
                                    {wt.direction}
                                  </Badge>
                                </TableCell>
                                <TableCell>{counterpartyCell}</TableCell>
                                <TableCell
                                  className={`text-right font-medium tabular-nums ${
                                    delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : ''
                                  }`}
                                >
                                  {saFormatSignedAmount(delta)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums text-muted-foreground">
                                  {wt.balanceBefore != null
                                    ? wt.balanceBefore.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 8,
                                      })
                                    : '—'}
                                </TableCell>
                                <TableCell className="text-right tabular-nums text-muted-foreground">
                                  {wt.balanceAfter != null
                                    ? wt.balanceAfter.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 8,
                                      })
                                    : '—'}
                                </TableCell>
                                <TableCell className="max-w-[14rem] truncate text-sm" title={wt.description ?? ''}>
                                  {wt.description ?? '—'}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-muted-foreground max-w-[10rem] break-all">
                                  {wt.referenceId ?? wt.transactionId ?? '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  {(() => {
                    const pag = wtListRes?.data?.pagination;
                    const totalPages = pag?.totalPages ?? 0;
                    if (!pag || totalPages <= 1) return null;
                    return (
                      <div className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 sm:flex-row">
                        <p className="text-sm text-muted-foreground">
                          Page {pag.page} of {totalPages} · {pag.total ?? 0} total
                        </p>
                        <NumberedPagination
                          currentPage={wtPage}
                          totalPages={totalPages}
                          onPageChange={setWtPage}
                          disabled={wtFetching}
                          variant="admin"
                        />
                      </div>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
      ) : activeTab === 'games' && canManage ? (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <div className="flex flex-row items-center gap-2 min-w-0">
              <Gamepad2 className="h-5 w-5 shrink-0 text-muted-foreground" />
              <CardTitle className="text-lg">Allowed games for this super agent</CardTitle>
            </div>
            {gamesUiMode === 'view' ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setGamesUiMode('edit')}>
                <Pencil className="h-4 w-4 mr-1.5" />
                Edit
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancelGamesEdit}
                disabled={putSuperAgentGames.isLoading}
              >
                Cancel
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Empty selection means all catalog games are allowed at this tier. Super agents and downstream agents can
              only assign subsets of this list.
            </p>
            {gamesLoading ? (
              <LoadingSpinner />
            ) : gamesUiMode === 'view' ? (
              <div className="space-y-3">
                <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
                  <span className="text-muted-foreground">Mode: </span>
                  <span className="font-medium">
                    {gameMode === 'all' ? 'All games (default)' : 'Selected games only'}
                  </span>
                </div>
                {gameMode === 'selected' ? (
                  <div className="max-h-72 overflow-y-auto rounded-md border p-3 space-y-2">
                    {selectedGamesViewRows.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No games selected.</p>
                    ) : (
                      selectedGamesViewRows.map((row) => (
                        <div key={row.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                          <span className="font-medium">{row.name}</span>
                          <span className="text-muted-foreground text-xs">({row.code})</span>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    The full catalog is available at this tier unless you restrict to selected games.
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="sa-game-mode"
                      checked={gameMode === 'all'}
                      onChange={() => setGameMode('all')}
                    />
                    All games (default)
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="sa-game-mode"
                      checked={gameMode === 'selected'}
                      onChange={() => setGameMode('selected')}
                    />
                    Selected games only
                  </label>
                </div>
                {gameMode === 'selected' ? (
                  <div className="max-h-72 overflow-y-auto rounded-md border p-3 space-y-2">
                    {gameProviderItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No game providers loaded.</p>
                    ) : (
                      gameProviderItems.map((p) => (
                        <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedGameIds.has(p.id)}
                            onChange={() => toggleGameId(p.id)}
                          />
                          <span>{p.name}</span>
                          <span className="text-muted-foreground text-xs">({p.code})</span>
                        </label>
                      ))
                    )}
                  </div>
                ) : null}
                <Button type="button" onClick={() => void handleSaveGames()} disabled={putSuperAgentGames.isLoading}>
                  {putSuperAgentGames.isLoading ? 'Saving…' : 'Save game list'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : activeTab === 'agents' || activeTab === 'shops' ? (
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            {activeTab === 'agents' ? (
              <>
                <Users className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">Agents under this super agent</CardTitle>
              </>
            ) : (
              <>
                <Store className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">Shops under managed agents</CardTitle>
              </>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {activeTab === 'agents' ? (
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Cost (%)</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableHead>Shop</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Cost (%)</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {activeTab === 'agents' ? (
                    agents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                          No agents assigned to this super agent.
                        </TableCell>
                      </TableRow>
                    ) : (
                      agents.map((agent) => (
                        <TableRow key={agent.id}>
                          <TableCell>
                            <Link
                              to={`/agents/${agent.id}`}
                              className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                            >
                              {agent.name}
                            </Link>
                          </TableCell>
                          <TableCell>{agent.email}</TableCell>
                          <TableCell>{rateToPercent(agent.costRate)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`uppercase text-xs tracking-wide ${statusClassName(agent.status)}`}>
                              {agent.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  ) : shops.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No shops under managed agents.
                      </TableCell>
                    </TableRow>
                  ) : (
                    shops.map((shop) => (
                      <TableRow key={shop.id}>
                        <TableCell>
                          <Link
                            to={`/shops/${shop.id}`}
                            className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                          >
                            {shop.name}
                          </Link>
                        </TableCell>
                        <TableCell>{shop.agentId ? agentNameById[shop.agentId] || shop.agentId : '—'}</TableCell>
                        <TableCell>{shop.email}</TableCell>
                        <TableCell>{rateToPercent(shop.costRate)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`uppercase text-xs tracking-wide ${statusClassName(shop.status)}`}>
                            {shop.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {canManage && superAgent.status === 'active' ? (
        <SellCreditsCashModal
          open={creditModalOpen}
          onClose={closeCreditModal}
          flow="admin_super_agent"
          buyerName={superAgent.name || superAgent.email || id}
          buyerCostRate={superAgentCostRate ?? 0}
          submitting={cashFunding.isLoading}
          error={cashFundingError}
          onSubmit={handleSellCreditsSubmit}
        />
      ) : null}

      {canManage && withdrawModalOpen && superAgent.status === 'active' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="super-agent-withdraw-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeWithdrawModal();
          }}
        >
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="space-y-3 pb-2">
              <div className="flex flex-row items-start justify-between gap-2">
                <CardTitle id="super-agent-withdraw-modal-title" className="text-lg flex items-center gap-2 pr-8">
                  <ArrowDownLeft className="h-5 w-5 text-primary" />
                  CDN credits (manual withdrawal)
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={closeWithdrawModal}
                  disabled={cashWithdrawal.isLoading}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="inline-flex w-fit items-center gap-1.5 rounded-md border border-yellow-300/90 bg-yellow-100/70 px-2.5 py-1">
                <AlertCircle className="h-[13px] w-[13px] text-amber-700" />
                <span className="text-[11px] font-medium text-amber-700">Admin action</span>
              </div>
              <p className="text-xs text-muted-foreground font-normal">
                Current balance:{' '}
                <strong className="text-foreground tabular-nums">
                  {superAgentWallet != null ? Number(superAgentWallet.balance).toFixed(2) : '0.00'}
                </strong>{' '}
                credits · Cash to pay out = credits × {superAgentCostRate == null ? '—' : `${(superAgentCostRate * 100).toFixed(0)}%`}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <form
                onSubmit={handleCashWithdrawalSubmit}
                className="grid gap-3 sm:grid-cols-[1fr_1.4fr] sm:items-end"
              >
                <div className="space-y-2 sm:col-span-1">
                  <label htmlFor="sa-withdraw-modal-amount" className="text-sm font-medium leading-none">
                    Credits to withdraw
                  </label>
                  <Input
                    id="sa-withdraw-modal-amount"
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="e.g. 100"
                    value={withdrawFromWallet}
                    onChange={(ev) => {
                      setWithdrawFromWallet(ev.target.value);
                      setCashWithdrawalError(null);
                    }}
                    disabled={cashWithdrawal.isLoading}
                    className="h-10 w-full"
                  />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <label htmlFor="sa-withdraw-modal-note" className="text-sm font-medium leading-none">
                    Note (optional)
                  </label>
                  <Input
                    id="sa-withdraw-modal-note"
                    type="text"
                    placeholder="Reason, recipient, etc."
                    value={withdrawNote}
                    onChange={(ev) => {
                      setWithdrawNote(ev.target.value);
                      setCashWithdrawalError(null);
                    }}
                    disabled={cashWithdrawal.isLoading}
                    className="h-10 w-full"
                  />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeWithdrawModal}
                    disabled={cashWithdrawal.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      cashWithdrawal.isLoading ||
                      !withdrawFromWallet ||
                      !Number.isFinite(parseFloat(withdrawFromWallet)) ||
                      parseFloat(withdrawFromWallet) <= 0
                    }
                  >
                    {cashWithdrawal.isLoading ? 'Recording…' : 'Record & withdraw'}
                  </Button>
                </div>
              </form>
              {cashWithdrawalError ? <p className="text-xs text-destructive">{cashWithdrawalError}</p> : null}
              {Number.isFinite(withdrawFromWalletNum) && withdrawFromWalletNum > 0 ? (
                <div
                  className="flex flex-wrap items-center gap-1.5 rounded-md bg-muted/70 px-3 py-2 text-xs text-muted-foreground"
                  role="status"
                  aria-live="polite"
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>Super agent gives up</span>
                  <strong className="font-medium text-foreground tabular-nums">{withdrawFromWalletNum.toFixed(2)} credits</strong>
                  <span>·</span>
                  <span>Cash to pay out</span>
                  <strong className="font-medium text-foreground tabular-nums">
                    {superAgentCostRate == null ? '—' : `$${(withdrawFromWalletNum * superAgentCostRate).toFixed(2)}`}
                  </strong>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {canManage && nameModalOpen && superAgent.status !== 'terminated' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="super-agent-name-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !patchSuperAgentName.isLoading) closeNameModal();
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="super-agent-name-modal-title" className="text-lg flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" />
                Edit display name
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeNameModal}
                disabled={patchSuperAgentName.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleNameSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="super-agent-display-name-input" className="text-sm font-medium">
                    Display name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="super-agent-display-name-input"
                    type="text"
                    autoComplete="organization"
                    value={nameDraft}
                    maxLength={DISPLAY_NAME_MAX_LENGTH}
                    onChange={(ev) => setNameDraft(ev.target.value)}
                    disabled={patchSuperAgentName.isLoading}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Letters, numbers, and spaces only. Max {DISPLAY_NAME_MAX_LENGTH} characters.
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeNameModal}
                    disabled={patchSuperAgentName.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={patchSuperAgentName.isLoading || nameDraft.trim() === ''}
                  >
                    {patchSuperAgentName.isLoading ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {canManage && costRateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="super-agent-cost-rate-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !patchSuperAgentRates.isLoading) setCostRateModalOpen(false);
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="super-agent-cost-rate-modal-title" className="text-lg flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" />
                Update cost rate
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCostRateModalOpen(false)}
                disabled={patchSuperAgentRates.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCostRateSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="super-agent-cost-rate-input" className="text-sm font-medium">
                    Cost rate (%)
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="super-agent-cost-rate-input"
                      type="number"
                      step="0.01"
                      min={0}
                      max={100}
                      value={costRatePercentInput}
                      onChange={(ev) => setCostRatePercentInput(ev.target.value)}
                      disabled={patchSuperAgentRates.isLoading}
                      className="max-w-[220px]"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCostRateModalOpen(false)}
                    disabled={patchSuperAgentRates.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={patchSuperAgentRates.isLoading || costRatePercentInput.trim() === ''}>
                    {patchSuperAgentRates.isLoading ? 'Saving…' : 'Update'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {canUpdateContact && id && superAgent.status !== 'terminated' ? (
        <AdminUpdateContactModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          target={{ kind: 'super_agent', superAgentId: id }}
          currentEmail={superAgent.email}
          currentPhone={superAgent.phone}
          emailVerified={Boolean(
            (superAgent as { emailVerified?: boolean; email_verified?: boolean }).emailVerified ??
              (superAgent as { email_verified?: boolean }).email_verified
          )}
          phoneVerified={Boolean(
            (superAgent as { phoneVerified?: boolean; phone_verified?: boolean }).phoneVerified ??
              (superAgent as { phone_verified?: boolean }).phone_verified
          )}
          title="Update super agent email / phone"
          onSuccess={() => {
            toast.success('Contact updated. Super agent must re-verify.');
            void queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, id]);
            void queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
          }}
        />
      ) : null}

      {canManageCredentials && id && superAgent.status !== 'terminated' ? (
        <AdminUpdateUsernameModal
          open={usernameModalOpen}
          onClose={() => setUsernameModalOpen(false)}
          target={{ kind: 'super_agent', superAgentId: id }}
          currentUsername={(superAgent as { username?: string | null }).username}
          title="Change super agent username"
          onSuccess={() => {
            toast.success('Username updated. Super agent sessions were signed out.');
            void queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, id]);
            void queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
          }}
        />
      ) : null}

      {canManage && passwordModalOpen && superAgent.status !== 'terminated' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="super-agent-password-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePasswordModal();
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="super-agent-password-modal-title" className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                Super agent login password
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closePasswordModal}
                disabled={patchSuperAgentPassword.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Set a new password for this super agent&apos;s portal login. All existing super agent sessions are signed
                out after the change.
              </p>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="super-agent-new-password-modal" className="text-sm font-medium">
                    New password
                  </label>
                  <div className="relative">
                    <Input
                      id="super-agent-new-password-modal"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={password}
                      onChange={(ev) => setPassword(ev.target.value)}
                      className="pr-10"
                      disabled={patchSuperAgentPassword.isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="super-agent-confirm-password-modal" className="text-sm font-medium">
                    Confirm password
                  </label>
                  <Input
                    id="super-agent-confirm-password-modal"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(ev) => setPasswordConfirm(ev.target.value)}
                    disabled={patchSuperAgentPassword.isLoading}
                  />
                </div>
                <p className="text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closePasswordModal}
                    disabled={patchSuperAgentPassword.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      patchSuperAgentPassword.isLoading ||
                      password.length < PASSWORD_MIN ||
                      passwordConfirm.length < PASSWORD_MIN
                    }
                  >
                    {patchSuperAgentPassword.isLoading ? 'Updating…' : 'Update password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      {canManage && saSuspendModalOpen && superAgent.status !== 'terminated' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sa-suspend-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !suspendSuperAgent.isLoading) closeSaSuspendModal();
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="sa-suspend-modal-title" className="text-lg flex items-center gap-2">
                <Ban className="h-5 w-5 text-amber-700" />
                Suspend super agent
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeSaSuspendModal}
                disabled={suspendSuperAgent.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Suspending <span className="font-medium text-foreground">{superAgent.name}</span> ends all active
                sessions. Type <span className="font-mono">{SA_SUSPEND_CONFIRM_WORD}</span> to confirm.
              </p>
              <div className="space-y-2">
                <label htmlFor="sa-suspend-confirm" className="text-sm font-medium">
                  Confirmation
                </label>
                <Input
                  id="sa-suspend-confirm"
                  value={saSuspendConfirmText}
                  onChange={(ev) => setSaSuspendConfirmText(ev.target.value)}
                  disabled={suspendSuperAgent.isLoading}
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={closeSaSuspendModal} disabled={suspendSuperAgent.isLoading}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleSaSuspendConfirmed()}
                  disabled={suspendSuperAgent.isLoading || !saSuspendPhraseOk}
                >
                  {suspendSuperAgent.isLoading ? 'Working…' : 'Suspend'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {canManage && saTerminateModalOpen && superAgent.status !== 'terminated' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sa-terminate-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !terminateSuperAgent.isLoading) closeSaTerminateModal();
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="sa-terminate-modal-title" className="text-lg flex items-center gap-2 text-destructive">
                <Skull className="h-5 w-5" />
                Terminate super agent
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeSaTerminateModal}
                disabled={terminateSuperAgent.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Terminating <span className="font-medium text-foreground">{superAgent.name}</span> is permanent. Managed
                agents that are still active will be suspended. Type <span className="font-mono">{SA_TERMINATE_CONFIRM_WORD}</span>{' '}
                to confirm.
              </p>
              <div className="space-y-2">
                <label htmlFor="sa-terminate-confirm" className="text-sm font-medium">
                  Confirmation
                </label>
                <Input
                  id="sa-terminate-confirm"
                  value={saTerminateConfirmText}
                  onChange={(ev) => setSaTerminateConfirmText(ev.target.value)}
                  disabled={terminateSuperAgent.isLoading}
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={closeSaTerminateModal} disabled={terminateSuperAgent.isLoading}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void handleSaTerminateConfirmed()}
                  disabled={terminateSuperAgent.isLoading || !saTerminatePhraseOk}
                >
                  {terminateSuperAgent.isLoading ? 'Working…' : 'Terminate'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <FinanceCorrectionDebitModal
        open={correctionOpen}
        onOpenChange={setCorrectionOpen}
        ownerType="super_agent"
        ownerId={id!}
        onApplied={() => {
          if (id) void queryClient.invalidateQueries(['superAgentDetail', id]);
          void queryClient.invalidateQueries({ queryKey: ['superAgentWalletTransactions', id] });
          void queryClient.invalidateQueries({ queryKey: ['usdWalletSummary'] });
        }}
      />
    </div>
  );
};

export default SuperAgentDetailPage;
